// Sprint runner-picker-on-create — choose-on-create modal that opens
// when the founder taps "+". Surfaces remaining capacity % per runner
// so the choice is informed; "Auto" is first + selected by default so
// the existing scheduler behaviour is one click away. Picking a
// specific runner pins the new agent to it via POST /api/agents/empty
// `runnerId`; the backend validates online + not-draining via
// validatePinnedRunner.
//
// Founder-first §5: capacity reasons rendered in plain Spanish; no
// raw status enums leaked. Cursor mirror §2.1: offline/draining
// rows are REPLACED with a one-line plain-language reason rather
// than greyed-out with a "no entry" cursor.
//
// Sprint runner-picker-canonical-usage — capacity source switched from
// heartbeat (`useRunnersStore.cap5hResidualPct`, often defaulted near
// 100 %) to the canonical Anthropic OAuth usage payload that the
// "Runner usage" modal already shows (`useRunnersUsageStore`).
// "% libre" = 100 - max(session 5h, weekly 7d, sonnet weekly) — see
// `lib/runner-capacity.ts` for the policy + tests.

import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from '../i18n';
import { useRunnersUsageStore } from '../stores/runners-usage.store';
import type { RunnerUsageEntry } from '../lib/api';
import { deriveCapacity } from '../lib/runner-capacity';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { deriveRunnerLabel } from './RunnerChip';

interface RunnerPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called when the founder confirms. `undefined` = Auto (scheduler).
  onConfirm: (runnerId: string | undefined) => void;
}

type RowKind = 'auto' | 'runner';

interface RunnerRow {
  kind: RowKind;
  runnerId?: string;
  hostname?: string;
  entry?: RunnerUsageEntry;
  disabledReason?: string;
}

function sortEntries(entries: RunnerUsageEntry[]): RunnerUsageEntry[] {
  // Numeric-aware collation so "runner-10" sorts after "runner-9", not
  // between "runner-1" and "runner-2" (plain localeCompare is
  // lexicographic — the ≥10-runner mis-order class, mirrored from
  // runners.repo.ts::sortRunnersNatural).
  return [...entries].sort((a, b) =>
    a.runnerId.localeCompare(b.runnerId, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

export function RunnerPickerModal({
  open,
  onOpenChange,
  onConfirm,
}: RunnerPickerModalProps) {
  const { t } = useTranslation();
  const entriesByRunnerId = useRunnersUsageStore((s) => s.entriesByRunnerId);
  const hydrate = useRunnersUsageStore((s) => s.hydrate);

  // Auto is the default. Selection survives until close so the
  // founder doesn't lose their pick if they tab away briefly.
  const [selected, setSelected] = useState<string | undefined>(undefined);

  // One-shot fresh refresh on open so the capacity reading is the
  // canonical Anthropic number, not a cached value. The 60 s
  // background poll (HomePage) keeps it warm afterwards.
  useEffect(() => {
    if (!open) return;
    void hydrate({ fresh: true });
    setSelected(undefined);
  }, [open, hydrate]);

  const rows: RunnerRow[] = useMemo(() => {
    const list: RunnerRow[] = [{ kind: 'auto' }];
    const entries = sortEntries(Object.values(entriesByRunnerId));
    for (const e of entries) {
      let disabledReason: string | undefined;
      if (e.status === 'offline') {
        disabledReason = t('runnerPicker.offlineReason');
      } else if (e.drainMode) {
        disabledReason = t('runnerPicker.drainingReason');
      }
      list.push({
        kind: 'runner',
        runnerId: e.runnerId,
        hostname: e.hostname,
        entry: e,
        disabledReason,
      });
    }
    return list;
  }, [entriesByRunnerId, t]);

  function handleConfirm() {
    onConfirm(selected);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Sprint picker-mobile-scroll: the DialogContent primitive has no
          max-height and is centred via `top-50% + translate-y-[-50%]`, so
          on a phone viewport the Auto row + 10 runner rows overflow past
          the top AND bottom edges and the footer (confirm button) lands
          off-screen — leaving mobile users with no way to advance. Same
          class + same fix as UsageModal (usage-limits-v2 D6): constrain
          the shell to 85vh and let the rows panel scroll while the header
          (title) and footer (cancel/confirm) stay pinned. `max-h-[85vh]`
          is a viewport unit — a structural calc allowed by
          design-discipline (§Allowed exceptions), not a raw pixel drift. */}
      <DialogContent
        className="flex flex-col max-h-[85vh] overflow-hidden"
        data-testid="runner-picker-modal"
      >
        <DialogHeader>
          <DialogTitle>{t('runnerPicker.title')}</DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto -mx-6 px-6 flex flex-col gap-2 py-2"
          data-testid="runner-picker-scroll"
        >
          <p className="text-caption text-muted-foreground">
            {t('runnerPicker.subtitle')}
          </p>

          <div className="flex flex-col gap-1.5" role="radiogroup">
            {rows.map((row) => {
              const isAuto = row.kind === 'auto';
              const id = isAuto ? '__auto__' : row.runnerId!;
              const checked = isAuto ? selected === undefined : selected === id;
              const disabled = !isAuto && !!row.disabledReason;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  aria-label={isAuto ? t('runnerPicker.autoLabel') : row.hostname}
                  onClick={() => {
                    if (disabled) return;
                    setSelected(isAuto ? undefined : id);
                  }}
                  disabled={disabled}
                  data-testid={
                    isAuto ? 'runner-picker-row-auto' : `runner-picker-row-${id}`
                  }
                  className={
                    'flex items-center gap-3 rounded-md border px-3 py-2.5 text-left ' +
                    'transition-colors ' +
                    (checked
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-card hover:bg-muted') +
                    (disabled ? ' opacity-60 cursor-not-allowed' : '')
                  }
                >
                  <span
                    aria-hidden="true"
                    className={
                      'shrink-0 w-4 h-4 rounded-full border ' +
                      (checked
                        ? 'border-accent bg-accent'
                        : 'border-border')
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-body font-medium truncate">
                      {isAuto
                        ? t('runnerPicker.autoLabel')
                        : `${deriveRunnerLabel(row.runnerId!)} · ${row.hostname}`}
                    </div>
                    {isAuto ? (
                      <div className="text-caption text-muted-foreground">
                        {t('runnerPicker.autoHelper')}
                      </div>
                    ) : disabled ? (
                      <div className="text-caption text-muted-foreground">
                        {row.disabledReason}
                      </div>
                    ) : (
                      <RunnerCapacity entry={row.entry!} t={t} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="runner-picker-cancel"
          >
            {t('runnerPicker.cancel')}
          </Button>
          <Button onClick={handleConfirm} data-testid="runner-picker-confirm">
            {t('runnerPicker.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunnerCapacity({
  entry,
  t,
}: {
  entry: RunnerUsageEntry;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // Sprint runner-picker-session-primary — primary % libre is session 5h
  // (the bucket that gates "can this runner serve my agent now?"),
  // subtitle exposes weekly + sonnet so a runner that's heavily
  // consumed over the longer horizon is still visible at a glance.
  const capacity = deriveCapacity(entry.usage);
  if (capacity === null) {
    // RPC failed for this runner OR session_5h is null — show plain-
    // language "unavailable" instead of a misleading numeric bar.
    return (
      <div className="text-caption text-muted-foreground">
        {t('runnerPicker.capacityUnknown')}
      </div>
    );
  }
  const { primaryFreePct, secondary } = capacity;
  const subtitleParts = secondary.map((b) => {
    if (b.label === 'weekly_scoped' && b.modelName) {
      return t('runnerPicker.bucketWeeklyScopedModel', {
        model: b.modelName,
        percent: b.freePct,
      });
    }
    if (b.label === 'weekly_scoped') {
      return t('runnerPicker.bucketWeeklyScoped', { percent: b.freePct });
    }
    if (b.label === 'weekly') return t('runnerPicker.bucketWeekly', { percent: b.freePct });
    return t('runnerPicker.bucketSonnet', { percent: b.freePct });
  });
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div
          aria-hidden="true"
          className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden"
        >
          <div
            className="h-full bg-accent"
            style={{ width: `${primaryFreePct}%` }}
          />
        </div>
        <span className="text-caption text-muted-foreground tabular-nums">
          {t('runnerPicker.capacityFree', { percent: primaryFreePct })}
        </span>
      </div>
      {subtitleParts.length > 0 && (
        <div className="text-caption text-muted-foreground tabular-nums">
          {subtitleParts.join(' · ')}
        </div>
      )}
    </div>
  );
}
